import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import AppsIcon from '@mui/icons-material/Apps';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { fetchApps } from '@/api/apps';
import type { LauncherApp } from '@/types/generated';

/**
 * The nine-dot menu.
 *
 * THE LIST COMES FROM THE SERVER, not from a constant here. The URLs are environment-driven and
 * only the backend reads them, the permission evaluation has to happen where the permissions are,
 * and the admin panel renders the same menu — a list hard-coded in this file is a list the other
 * application drifts from within a month.
 *
 * TOOLING IS SEPARATED AND LABELLED rather than filtered into the same grid. These are a
 * developer's stack, they are not present in a real deployment, and someone glancing at the menu
 * should be able to tell that at once — not discover it when a link they sent a customer 404s.
 *
 * FETCHED ON OPEN. A menu nobody clicked should not cost a request on every page load.
 */

const ICONS: Record<string, typeof AppsIcon> = {
  quote: DescriptionOutlinedIcon,
  admin: AdminPanelSettingsOutlinedIcon,
  api: ApiOutlinedIcon,
  queue: LayersOutlinedIcon,
  model: PsychologyOutlinedIcon,
  storage: InventoryOutlinedIcon,
  mail: MailOutlineIcon,
};

export function AppLauncher() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const { data: apps = [], isLoading } = useQuery({
    queryKey: ['apps'],
    queryFn: fetchApps,
    enabled: open,
    // The set changes when someone's role changes, which is rare and never mid-session.
    staleTime: 5 * 60_000,
  });

  const product = apps.filter((app) => app.group === 'product');
  const tooling = apps.filter((app) => app.group === 'tooling');

  return (
    <>
      <Tooltip title="Applications">
        <IconButton
          size="small"
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Applications"
          aria-haspopup="true"
        >
          <AppsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { width: 340, p: 1 } } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3 }} align="center">
            Loading…
          </Typography>
        )}

        {!isLoading && apps.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3 }} align="center">
            Nothing else is available to you here.
          </Typography>
        )}

        {product.length > 0 && <AppGrid apps={product} onNavigate={() => setAnchorEl(null)} />}

        {tooling.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                px: 1,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Developer tools
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', px: 1, pb: 0.5 }}>
              Local stack only — not present in a deployment.
            </Typography>
            <AppGrid apps={tooling} onNavigate={() => setAnchorEl(null)} />
          </>
        )}
      </Menu>
    </>
  );
}

function AppGrid({ apps, onNavigate }: { apps: LauncherApp[]; onNavigate: () => void }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
      {apps.map((app) => {
        const Icon = ICONS[app.icon] ?? AppsIcon;

        return (
          <Tooltip key={app.key} title={app.description} placement="bottom">
            <ButtonBase
              component="a"
              href={app.url}
              // `noopener` is not decoration on an external target: without it the opened page
              // gets a handle on this one through `window.opener` and can navigate it elsewhere.
              {...(app.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              onClick={onNavigate}
              sx={{
                flexDirection: 'column',
                gap: 0.75,
                borderRadius: 1,
                p: 1.5,
                textAlign: 'center',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Icon sx={{ fontSize: 26, color: 'text.secondary' }} />
              <Stack direction="row" spacing={0.25} alignItems="center">
                <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {app.name}
                </Typography>
                {app.external && (
                  <OpenInNewIcon sx={{ fontSize: 11, color: 'text.disabled' }} aria-hidden />
                )}
              </Stack>
            </ButtonBase>
          </Tooltip>
        );
      })}
    </Box>
  );
}
